import React, { useState, useEffect } from 'react';
import { createBrowserRouter, matchRoutes, RouterProvider } from 'react-router-dom';
import affiliate from '../modules/affiliate';
import { getTRS } from '../modules/cookies';
import {
  setAffiliateId,
  setEasyMarketingTrackId,
  preselectByQuery,
  Query,
} from '../redux/actions/global';
import { trackToBunchBox } from '../modules/tracking';
import { setExperimentByQuery } from '../redux/actions/experiments';
import { AppStore } from '../definitions/types';
import { getWindow } from '../modules/helpers';
import { unsetGlobalError } from '../redux/actions/errors';
import { getAppRoutes } from '../routes';
import { getAllowedDestination } from '../routes/route-helpers';
import LoadingIndicatorComponent from '../views/loadingIndicator/loadingIndicator';
import { forwardToDolphinCms } from '../redux/actions/dolphin-cms';
import { changeViewToIndustry } from '../redux/actions/router';
import { Navigation, stripBasename } from '../modules/navigation';

type AppRouter = ReturnType<typeof createBrowserRouter>;

interface Props {
  store: AppStore;
  navigation: Navigation;
}

export const parseQuery = (search: string): Query => {
  const query: { [key: string]: string | string[] } = {};
  new URLSearchParams(search).forEach((value: string, key: string) => {
    const previous = query[key];
    query[key] = previous === undefined ? value : ([] as string[]).concat(previous, value);
  });
  return query as Query;
};

// The entry URL is read once, before any view renders (React Router 3 ran this as the root's onEnter).
export const enterApp = (store: AppStore, navigation: Navigation, location: Location): void => {
  const state = store.getState();
  const query = parseQuery(location.search);
  const ref = affiliate(query, location.hash);
  const trsId = getTRS();
  if (ref) {
    store.dispatch(setAffiliateId(ref));
  }

  if (trsId) {
    store.dispatch(setEasyMarketingTrackId(trsId));
  }

  if (query.product === 'dolphin') {
    store.dispatch(forwardToDolphinCms());
  } else if (query.product === 'creator') {
    store.dispatch(changeViewToIndustry());
  }
  trackToBunchBox('evaluate experiments');

  store.dispatch(preselectByQuery(query));
  store.dispatch(setExperimentByQuery(query));

  const pathname = stripBasename(location.pathname, navigation.basename);
  const allowedPath = getAllowedDestination(pathname, state);
  if (allowedPath !== pathname) {
    window.history.replaceState(null, '', `${navigation.basename}${allowedPath}`);
  }
};

// Every later navigation scrolls to the top and clears the global error (the root's onChange in React Router 3).
export const handleLocationChanges = (store: AppStore, router: AppRouter): (() => void) => {
  let { key, pathname } = router.state.location;

  return router.subscribe(({ location }) => {
    if (location.key === key) {
      return;
    }
    const win = getWindow();
    if (win && location.pathname !== pathname) {
      win.scrollTo(0, 0);
    }
    key = location.key;
    pathname = location.pathname;

    store.dispatch(unsetGlobalError());
  });
};

export const createAppRouter = (store: AppStore, navigation: Navigation): AppRouter => {
  const routes = getAppRoutes(store);
  // Unknown paths render nothing, without the entry logic, as in React Router 3.
  if (matchRoutes(routes, stripBasename(window.location.pathname, navigation.basename))) {
    enterApp(store, navigation, window.location);
  }

  const router = createBrowserRouter([...routes, { path: '*', element: null }], {
    basename: navigation.basename,
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  });
  navigation.attach(router);
  handleLocationChanges(store, router);

  return router;
};

export const Router: React.FunctionComponent<Props> = ({ store, navigation }) => {
  const [router, setRouter] = useState<AppRouter | null>(null);

  useEffect(() => {
    setRouter(createAppRouter(store, navigation));
  }, []);

  if (!router) {
    return <LoadingIndicatorComponent />;
  }

  return (
    <RouterProvider
      router={router}
      fallbackElement={<LoadingIndicatorComponent />}
      future={{ v7_startTransition: true }}
    />
  );
};
